import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from "recharts";
import PageLayout from "../components/PageLayout";
import LoadingOverlay from "../components/LoadingOverlay";
import { useSharedDataset } from "../DataContext";
import { theme } from "../theme";

// ── Constants & helpers ───────────────────────────────────────────────────────

const WS_URL = process.env.NODE_ENV === "development"
  ? "ws://localhost:8000/ws/stream"
  : null; // Production uses client-side simulation (Railway/Render don't support WS on free tier)

const LIVE_ATTACK_TYPES = [
  ["dos","critical"],["ddos","critical"],["syn_flood","critical"],
  ["brute_force","critical"],["sql_injection","critical"],["exploit_attempt","critical"],
  ["malware_c2","critical"],["port_scan","medium"],["nmap_scan","medium"],
  ["arp_poisoning","high"],["mitm","high"],["data_exfiltration","high"],
  ["c2_beacon","high"],["lateral_movement","high"],["reconnaissance","medium"],
  ["fuzzing","medium"],["normal","info"],["normal","info"],
  ["normal","info"],["normal","info"],
];
const PROTOCOLS = ["TCP","UDP","ICMP","HTTP","HTTPS","DNS","SSH","FTP"];
const ACTIONS   = ["BLOCK","BLOCK","ALLOW","ALERT","DROP"];
const SENSORS   = ["fw-01","ids-02","zeek-03","suricata-04","cisco-asa-05","fortinet-06"];

function randInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function randIP(priv) {
  return priv
    ? `192.168.${randInt(1,254)}.${randInt(1,254)}`
    : `${randInt(1,223)}.${randInt(0,255)}.${randInt(0,255)}.${randInt(1,254)}`;
}
function generateAlert() {
  const [attack_type, severity] = LIVE_ATTACK_TYPES[randInt(0, LIVE_ATTACK_TYPES.length - 1)];
  const inbound = Math.random() > 0.3;
  return {
    event_id:    randInt(100000, 999999),
    attack_type, severity,
    src_ip:      inbound ? randIP(false) : randIP(true),
    dst_ip:      inbound ? randIP(true)  : randIP(false),
    src_port:    randInt(1024, 65535),
    dst_port:    [22,23,80,443,445,3389,8080,8443,53,21,25][randInt(0,10)],
    protocol:    PROTOCOLS[randInt(0, PROTOCOLS.length - 1)],
    action:      ACTIONS[randInt(0, ACTIONS.length - 1)],
    bytes:       randInt(64, 65000),
    packets:     randInt(1, 500),
    timestamp:   new Date().toISOString(),
    sensor:      SENSORS[randInt(0, SENSORS.length - 1)],
  };
}
const LIVE_MAX     = 200; // ring-buffer cap for live alerts



const C = theme.colors.chartColors || [
  "#1565C0","#C62828","#2E7D32","#F57F17","#6A1B9A",
  "#00695C","#AD1457","#0277BD","#4E342E","#37474F",
];

const SEVERITY_COLORS = {
  critical: "#FF0033",
  high:     "#FF3D57",
  medium:   "#FFB800",
  low:      "#00D4FF",
  info:     "#6CA2AF",
  unknown:  "#3D5A80"
};

const EXECUTIVE_SUMMARY_COLORS = {
  total:    "#4F746C",
  critical: "#EF3333",
  high:     "#FF8C00",
  medium:   "#F9A825",
  low:      "#2E9D44",
  info:     "#6CA2AF"
};

// Attack-type to severity mapping — used when no severity column exists
const ATTACK_SEVERITY_MAP = {
  // Critical
  dos: "critical", ddos: "critical", ransomware: "critical",
  exploit: "critical", backdoor: "critical", rootkit: "critical",
  bruteforce: "critical", brute_force: "critical", metasploit: "critical",
  injection: "critical", overflow: "critical", shellcode: "critical",
  slowloris: "critical", synhping: "critical", syn_hping: "critical",
  // High
  arp: "high", poisoning: "high", spoofing: "high", mitm: "high",
  malware: "high", trojan: "high", worm: "high", botnet: "high",
  phishing: "high", scan: "medium", nmap: "medium",
  // Medium
  probe: "medium", reconnaissance: "medium", sweep: "medium",
  fuzzing: "medium", discovery: "medium",
  // Low / Info
  normal: "low", benign: "info", legitimate: "info",
  thing_speak: "info", thingspeak: "info",
  wipro: "info", mqtt: "low", publish: "low",
};

function deriveAttackSeverity(attackName) {
  const v = String(attackName || "").toLowerCase().replace(/[\s_-]/g, "");
  for (const [key, sev] of Object.entries(ATTACK_SEVERITY_MAP)) {
    if (v.includes(key.replace(/[\s_-]/g, ""))) return sev;
  }
  return "medium"; // default for unknown attack types
}

const getSeverityLevel = (row, col, attackCol) => {
  // 1. Try explicit severity column first
  if (col) {
    const v = String(row[col] || "").toLowerCase();
    if (v.includes("critical") || v === "5" || v === "emergency") return "critical";
    if (v.includes("high") || v === "4" || v === "3")             return "high";
    if (v.includes("med") || v === "2")                           return "medium";
    if (v.includes("low") || v === "1")                           return "low";
    if (v.includes("info") || v === "0")                          return "info";
    if (v && v !== "unknown") return "medium";
  }
  // 2. Derive from attack type column if no severity col
  if (attackCol && row[attackCol]) {
    return deriveAttackSeverity(String(row[attackCol]));
  }
  return "unknown";
};

const Card = ({ children, style = {} }) => (
  <div style={{
    background: theme.colors.card,
    border: `1px solid ${theme.colors.cardBorder}`,
    borderRadius: 12, padding: 20, marginBottom: 20, ...style
  }}>{children}</div>
);

const ChartTitle = ({ children }) => (
  <div style={{
    fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
    color: theme.colors.accent, letterSpacing: 1, marginBottom: 16, textTransform: "uppercase"
  }}>{children}</div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`,
      borderRadius: 8, padding: "10px 14px", fontFamily: theme.fonts.mono, fontSize: 12
    }}>
      <div style={{ color: theme.colors.textSecondary, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || theme.colors.accent }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

function countBy(data, col, topN = 20) {
  if (!col) return [];
  const counts = {};
  data.forEach(r => {
    const k = String(r[col] ?? "Unknown").trim() || "Unknown";
    counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1]).slice(0, topN)
    .map(([name, value]) => ({ name, value }));
}

function detectAlertColumns(cols) {
  const find = (...kw) => cols.find(c => kw.some(k => c.toLowerCase().replace(/[\s_-]/g, "").includes(k)));
  return {
    severity:    find("severity", "priority", "risk", "level", "urgency"),
    alertName:   find("alertname", "rulename", "signature", "alert", "rule", "message", "msg", "name"),
    action:      find("action", "verdict", "disposition", "response", "status"),
    srcIP:       find("srcip", "sourceip", "src_ip", "source", "attackerip"),
    dstIP:       find("dstip", "destip", "dst_ip", "destination", "victimip"),
    srcPort:     find("srcport", "sport", "src_port"),
    dstPort:     find("dstport", "dport", "dst_port", "destport"),
    protocol:    find("protocol", "proto"),
    category:    find("category", "class", "type", "threat", "attacktype"),
    timestamp:   find("timestamp", "time", "datetime", "date", "ts", "logtime"),
    user:        find("user", "username", "account"),
    interface:   find("interface", "iface", "intf", "zone"),
    country:     find("country", "geo", "location"),
    application: find("app", "application", "service"),
    bytes:       find("bytes", "size", "payload"),
    device:      find("device", "sensor", "probe", "collector", "hostname", "host")
  };
}

// ── Severity badge ────────────────────────────────────────────────────────────

const SeverityBadge = ({ level }) => {
  const color = EXECUTIVE_SUMMARY_COLORS[level] || EXECUTIVE_SUMMARY_COLORS.total;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      background: `${color}22`, border: `1px solid ${color}55`,
      color, fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 1
    }}>{level}</span>
  );
};

// ── Alert row ─────────────────────────────────────────────────────────────────

const AlertRow = ({ row, detected, index }) => {
  const [expanded, setExpanded] = useState(false);
  const level = getSeverityLevel(row, detected.severity, detected.category || detected.alertName);
  const color = EXECUTIVE_SUMMARY_COLORS[level] || EXECUTIVE_SUMMARY_COLORS.total;

  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        style={{
          background: index % 2 === 0 ? `${color}09` : `${color}05`,
          borderBottom: `1px solid ${theme.colors.cardBorder}33`,
          cursor: "pointer",
          transition: "background 0.15s"
        }}
        onMouseEnter={e => e.currentTarget.style.background = `${color}18`}
        onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? `${color}09` : `${color}05`}
      >
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
          #{index + 1}
        </td>
        <td style={{ padding: "9px 12px" }}>
          <SeverityBadge level={level} />
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.body, fontSize: 12, color: theme.colors.textPrimary, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {detected.alertName ? String(row[detected.alertName] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.danger }}>
          {detected.srcIP ? String(row[detected.srcIP] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.warning }}>
          {detected.dstIP ? String(row[detected.dstIP] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }}>
          {detected.dstPort ? String(row[detected.dstPort] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
          {detected.protocol ? String(row[detected.protocol] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 11, color: detected.action && String(row[detected.action] || "").toLowerCase().includes("block") ? theme.colors.danger : theme.colors.success }}>
          {detected.action ? String(row[detected.action] || "—") : "—"}
        </td>
        <td style={{ padding: "9px 12px", fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>
          {detected.timestamp ? String(row[detected.timestamp] || "—").slice(0, 19) : "—"}
        </td>
        <td style={{ padding: "9px 12px", color: theme.colors.textMuted, fontSize: 14 }}>
          {expanded ? "▲" : "▼"}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={10} style={{
            background: theme.colors.inputBg,
            padding: "12px 24px",
            borderBottom: `1px solid ${theme.colors.cardBorder}`
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 8, fontFamily: theme.fonts.mono, fontSize: 11
            }}>
              {Object.entries(row).map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: 6 }}>
                  <span style={{ color: theme.colors.accent, flexShrink: 0 }}>{k}:</span>
                  <span style={{ color: theme.colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {String(v ?? "—")}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function CyberAlerts() {
  const { dataset, setDataset, hasData } = useSharedDataset();
  const rawData  = dataset.data || [];
  const columns  = dataset.columns || [];
  const fileName = dataset.fileName || "";
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState("");
  const [filterSev, setFilterSev] = useState("ALL");
  const [filterAction, setFilterAction] = useState("ALL");
  const [sortField, setSortField] = useState(null);
  const [sortAsc, setSortAsc]   = useState(false);
  const [page, setPage]         = useState(1);
  const [rowsPerPage]           = useState(50);
  const [activeTab, setActiveTab] = useState("alerts");

  // ── Live Feed state ────────────────────────────────────────────────────────
  const [wsStatus, setWsStatus]     = useState("disconnected"); // disconnected | connecting | connected | error
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [isPaused, setIsPaused]     = useState(false);
  const [liveCount, setLiveCount]   = useState(0);
  const [liveSevCounts, setLiveSevCounts] = useState({ critical:0, high:0, medium:0, low:0, info:0 });
  const wsRef      = useRef(null);
  const pausedRef  = useRef(false);  // ref so WS handler always reads current value
  const feedEndRef = useRef(null);   // auto-scroll anchor

  const simIntervalRef  = useRef(null);

  // ── Demo Mode state ──────────────────────────────────────────────────────
  const [demoMode,     setDemoMode]     = useState(false);
  const [demoData,     setDemoData]     = useState([]);
  const [demoFileName, setDemoFileName] = useState("");
  const [demoSpeed,    setDemoSpeed]    = useState(5);
  const [demoPos,      setDemoPos]      = useState(0);
  const [demoRunning,  setDemoRunning]  = useState(false);
  const [demoLoading,  setDemoLoading]  = useState(false);
  const demoPosRef      = useRef(0);
  const demoIntervalRef = useRef(null);
  const demoDataRef     = useRef([]);

  const connectWS = useCallback(() => {
    // Development: use real WebSocket backend
    if (WS_URL) {
      if (wsRef.current && wsRef.current.readyState < 2) return;
      setWsStatus("connecting");
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen    = () => setWsStatus("connected");
      ws.onmessage = (e) => {
        if (pausedRef.current) return;
        try {
          const alert = JSON.parse(e.data);
          const sev   = deriveAttackSeverity(alert.attack_type || alert.severity || "");
          const entry = { ...alert, _sev: sev, _ts: new Date().toLocaleTimeString() };
          setLiveAlerts(prev => { const next = [entry, ...prev]; return next.length > LIVE_MAX ? next.slice(0, LIVE_MAX) : next; });
          setLiveCount(c => c + 1);
          setLiveSevCounts(prev => ({ ...prev, [sev]: (prev[sev] || 0) + 1 }));
        } catch (_) {}
      };
      ws.onerror = () => setWsStatus("error");
      ws.onclose = () => { setWsStatus("disconnected"); wsRef.current = null; };
      return;
    }

    // Production: client-side simulation (no WebSocket needed)
    setWsStatus("connecting");
    setTimeout(() => {
      setWsStatus("connected");
      simIntervalRef.current = setInterval(() => {
        if (pausedRef.current) return;
        const alert = generateAlert();
        const sev   = deriveAttackSeverity(alert.attack_type || alert.severity || "");
        const entry = { ...alert, _sev: sev, _ts: new Date().toLocaleTimeString() };
        setLiveAlerts(prev => { const next = [entry, ...prev]; return next.length > LIVE_MAX ? next.slice(0, LIVE_MAX) : next; });
        setLiveCount(c => c + 1);
        setLiveSevCounts(prev => ({ ...prev, [sev]: (prev[sev] || 0) + 1 }));
      }, 800 + Math.random() * 1700);
    }, 600);
  }, []);

  const disconnectWS = useCallback(() => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (simIntervalRef.current) { clearInterval(simIntervalRef.current); simIntervalRef.current = null; }
    setWsStatus("disconnected");
  }, []);

  // ── Demo Mode Functions ───────────────────────────────────────────────────

  const loadDemoFile = useCallback((e) => {
    const f = e.target.files[0];
    if (!f) return;
    setDemoLoading(true);
    setDemoFileName(f.name);

    const processRows = (rows) => {
      demoDataRef.current = rows;
      setDemoData(rows);
      setDemoPos(0);
      demoPosRef.current = 0;
      setDemoLoading(false);
    };

    if (f.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(f, {
        header: true, skipEmptyLines: true,
        complete: (res) => processRows(res.data)
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const wb    = XLSX.read(evt.target.result);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        processRows(XLSX.utils.sheet_to_json(sheet));
      };
      reader.readAsArrayBuffer(f);
    }
  }, []);

  // Convert a dataset row to a live alert event
  const rowToAlert = useCallback((row, idx) => {
    const cols = Object.keys(row).map(k => k.toLowerCase().trim());
    const get  = (...candidates) => {
      for (const c of candidates) {
        const found = Object.keys(row).find(k => k.toLowerCase().trim() === c.toLowerCase().trim()
          || k.toLowerCase().trim().includes(c.toLowerCase()));
        if (found && row[found] !== undefined && row[found] !== "") return String(row[found]).trim();
      }
      return null;
    };

    const rawLabel   = get("label","type","attack_type","attack","class","category","signature") || "unknown";
    const sev        = deriveAttackSeverity(rawLabel);
    const srcIP      = get("src_ip","sourceip","source ip","id.orig_h","src") || randIP(false);
    const dstIP      = get("dst_ip","destip","destination ip","id.resp_h","dst") || randIP(true);
    const dstPort    = get("dst_port","destination port","dport","id.resp_p") || String([22,23,80,443,445,3389,8080,53,21][Math.floor(Math.random()*9)]);
    const srcPort    = get("src_port","source port","sport","id.orig_p") || String(randInt(1024,65535));
    const proto      = get("protocol","proto","transport") || PROTOCOLS[Math.floor(Math.random()*PROTOCOLS.length)];
    const bytes      = get("total length","bytes","length","size","totlen fwd pkts") || String(randInt(64,65000));
    const cleanLabel = rawLabel.replace(/[^ -]/g,"").replace(/\s+/g," ").trim() || "unknown";

    return {
      event_id:    1000000 + idx,
      attack_type: cleanLabel,
      severity:    sev,
      src_ip:      srcIP,
      dst_ip:      dstIP,
      src_port:    parseInt(srcPort) || randInt(1024,65535),
      dst_port:    parseInt(dstPort) || 80,
      protocol:    proto.toUpperCase(),
      action:      sev === "critical" ? "BLOCK" : sev === "high" ? "ALERT" : sev === "info" ? "ALLOW" : "ALERT",
      bytes:       parseInt(bytes) || randInt(64,65000),
      sensor:      "demo-feed",
      _sev:        sev,
      _ts:         new Date().toLocaleTimeString(),
      _demo:       true,
    };
  }, []);

  const startDemo = useCallback(() => {
    if (!demoDataRef.current.length) return;
    disconnectWS(); // stop random sim if running
    setWsStatus("connected");
    setDemoRunning(true);

    const interval = Math.max(50, Math.round(1000 / demoSpeed));
    demoIntervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const rows = demoDataRef.current;
      if (!rows.length) return;

      const pos = demoPosRef.current;
      const row = rows[pos];
      const alert = rowToAlert(row, pos);

      setLiveAlerts(prev => { const next = [alert, ...prev]; return next.length > LIVE_MAX ? next.slice(0, LIVE_MAX) : next; });
      setLiveCount(c => c + 1);
      setLiveSevCounts(prev => ({ ...prev, [alert._sev]: (prev[alert._sev] || 0) + 1 }));

      const next = pos + 1;
      if (next >= rows.length) {
        demoPosRef.current = 0; // loop
        setDemoPos(0);
      } else {
        demoPosRef.current = next;
        setDemoPos(next);
      }
    }, interval);
  }, [demoSpeed, disconnectWS, rowToAlert]);

  const stopDemo = useCallback(() => {
    if (demoIntervalRef.current) { clearInterval(demoIntervalRef.current); demoIntervalRef.current = null; }
    setDemoRunning(false);
    setWsStatus("disconnected");
  }, []);

  // Cleanup demo on unmount
  useEffect(() => () => {
    if (demoIntervalRef.current) clearInterval(demoIntervalRef.current);
  }, []);

  const togglePause = useCallback(() => {
    pausedRef.current = !pausedRef.current;
    setIsPaused(p => !p);
  }, []);

  const clearLive = useCallback(() => {
    setLiveAlerts([]);
    setLiveCount(0);
    setLiveSevCounts({ critical:0, high:0, medium:0, low:0, info:0 });
  }, []);

  const resetDemo = useCallback(() => {
    stopDemo();
    demoPosRef.current = 0;
    setDemoPos(0);
    clearLive();
  }, [stopDemo, clearLive]);

  // Cleanup on unmount
  useEffect(() => () => {
    wsRef.current?.close();
    if (simIntervalRef.current) clearInterval(simIntervalRef.current);
  }, []);



  // ── File Upload ──────────────────────────────────────────────────────────────

  const handleFile = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const done = (data) => {
      setDataset({ data, columns: Object.keys(data[0] || {}), fileName: file.name, file });
      setLoading(false);
      setPage(1);
    };
    if (file.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(file, { header: true, skipEmptyLines: true, complete: r => done(r.data) });
    } else {
      const reader = new FileReader();
      reader.onload = evt => {
        const wb = XLSX.read(evt.target.result);
        done(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]));
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const detected = useMemo(() => {
    const d = detectAlertColumns(columns);
    // If no alertName found, use attack/category column as alert name
    if (!d.alertName) {
      d.alertName = columns.find(c =>
        ["attack_type","attacktype","attack","label","class","category","signature","threat"]
        .some(k => c.toLowerCase().replace(/[\s_-]/g,"").includes(k))
      ) || null;
    }
    // If no category, use same
    if (!d.category) d.category = d.alertName;
    return d;
  }, [columns]);

  // ── Severity counts ───────────────────────────────────────────────────────────

  const severityCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0, unknown: 0 };
    rawData.forEach(r => {
      counts[getSeverityLevel(r, detected.severity, detected.category || detected.alertName)]++;
    });
    return counts;
  }, [rawData, detected.severity, detected.category, detected.alertName]);

  // ── Actions list ──────────────────────────────────────────────────────────────

  const actionValues = useMemo(() => {
    if (!detected.action) return [];
    return [...new Set(rawData.map(r => String(r[detected.action] || "").trim()).filter(Boolean))];
  }, [rawData, detected.action]);

  // ── Filtered + sorted ─────────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    let d = rawData;
    if (filterSev !== "ALL") d = d.filter(r => getSeverityLevel(r, detected.severity, detected.category || detected.alertName) === filterSev);
    if (filterAction !== "ALL" && detected.action) d = d.filter(r => String(r[detected.action] || "").trim() === filterAction);
    if (search) d = d.filter(r => Object.values(r).join(" ").toLowerCase().includes(search.toLowerCase()));
    if (sortField) {
      d = [...d].sort((a, b) => {
        const av = String(a[sortField] ?? "");
        const bv = String(b[sortField] ?? "");
        return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return d;
  }, [rawData, filterSev, filterAction, search, sortField, sortAsc, detected]);

  // ── Pagination ────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const pageData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // ── Charts ────────────────────────────────────────────────────────────────────

  const charts = useMemo(() => {
    const d = filteredData;
    if (!d.length) return {};

    const severityBar = Object.entries(severityCounts)
      .filter(([, v]) => v > 0)
      .map(([k, v], i) => ({ name: k, value: v, fill: C[i % C.length] }));

    const alertNameDist = countBy(d, detected.alertName, 15);
    const categoryDist  = countBy(d, detected.category, 15);
    const actionDist    = countBy(d, detected.action, 10);
    const protocolDist  = countBy(d, detected.protocol, 10);
    const srcIPDist     = countBy(d, detected.srcIP, 15);
    const dstIPDist     = countBy(d, detected.dstIP, 15);
    const dstPortDist   = countBy(d, detected.dstPort, 15);
    const countryDist   = countBy(d, detected.country, 15);
    const deviceDist    = countBy(d, detected.device, 10);
    const appDist       = countBy(d, detected.application, 10);

    // Timeline: events per bucket
    const BUCKETS = 60;
    const bucketCounts = { critical: Array(BUCKETS).fill(0), high: Array(BUCKETS).fill(0), medium: Array(BUCKETS).fill(0) };
    d.forEach((row, i) => {
      const b = Math.floor((i / d.length) * BUCKETS);
      const lv = getSeverityLevel(row, detected.severity, detected.category || detected.alertName);
      if (lv === "critical") bucketCounts.critical[b]++;
      else if (lv === "high") bucketCounts.high[b]++;
      else if (lv === "medium") bucketCounts.medium[b]++;
    });
    const timeline = Array.from({ length: BUCKETS }, (_, i) => ({
      t: i,
      Critical: bucketCounts.critical[i],
      High:     bucketCounts.high[i],
      Medium:   bucketCounts.medium[i]
    }));

    // SrcIP x severity heat
    const ipSeverity = [];
    if (detected.srcIP) {
      const map = {};
      d.forEach(r => {
        const ip = String(r[detected.srcIP] || "Unknown").trim();
        const lv = getSeverityLevel(r, detected.severity, detected.category || detected.alertName);
        if (!map[ip]) map[ip] = { critical: 0, high: 0, medium: 0, low: 0 };
        if (map[ip][lv] !== undefined) map[ip][lv]++;
      });
      Object.entries(map).sort((a, b) => {
        const as = a[1].critical * 4 + a[1].high * 3 + a[1].medium * 2 + a[1].low;
        const bs = b[1].critical * 4 + b[1].high * 3 + b[1].medium * 2 + b[1].low;
        return bs - as;
      }).slice(0, 10).forEach(([ip, v]) => {
        ipSeverity.push({ name: ip, ...v });
      });
    }

    return {
      severityBar, alertNameDist, categoryDist, actionDist,
      protocolDist, srcIPDist, dstIPDist, dstPortDist,
      countryDist, deviceDist, appDist, timeline, ipSeverity
    };
  }, [filteredData, detected, severityCounts]);

  const tabs = [
    { id: "alerts",    label: "Alert Feed" },
    { id: "summary",   label: "Summary" },
    { id: "network",   label: "Network" },
    { id: "timeline",  label: "Timeline" },
    { id: "live",      label: "⬤ Live Feed" },
  ];

  // ── Export ────────────────────────────────────────────────────────────────────

  const exportCSV = () => {
    if (!filteredData.length) return;
    const headers = Object.keys(filteredData[0]);
    const csv = [headers.join(","), ...filteredData.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "nexgen_alerts.csv";
    a.click();
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <PageLayout title="Cyber Alerts" subtitle="IDS / FIREWALL / SIEM · ALERT ANALYSIS">
      <LoadingOverlay visible={loading} fileName={fileName} />

      {/* ── Toolbar ───────────────────────────────────────────────────────────── */}
      <Card style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{
          padding: "10px 20px",
          background: "#C63434",
          borderRadius: 8, cursor: "pointer",
          fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
          color: theme.colors.buttonText, letterSpacing: 1, whiteSpace: "nowrap"
        }}>
          ⬆ LOAD ALERTS
          <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
        </label>

        {/* Live Feed toggle button */}
        <button
          onClick={() => { setActiveTab("live"); wsStatus === "disconnected" && connectWS(); }}
          style={{
            padding: "10px 20px",
            background: wsStatus === "connected"
              ? `linear-gradient(135deg, ${SEVERITY_COLORS.critical}, #990022)`
              : "rgba(255,0,51,0.12)",
            border: `1px solid ${wsStatus === "connected" ? SEVERITY_COLORS.critical : "rgba(255,0,51,0.4)"}`,
            borderRadius: 8, cursor: "pointer",
            fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
            color: wsStatus === "connected" ? "#fff" : SEVERITY_COLORS.critical,
            letterSpacing: 1, whiteSpace: "nowrap",
            display: "flex", alignItems: "center", gap: 6
          }}
        >
          <span style={{
            width: 7, height: 7, borderRadius: "50%",
            background: wsStatus === "connected" ? "#fff"
              : wsStatus === "connecting" ? theme.colors.warning
              : wsStatus === "error" ? theme.colors.danger : "#3D5A80",
            display: "inline-block",
            boxShadow: wsStatus === "connected" ? "0 0 6px #fff" : "none",
            animation: wsStatus === "connected" ? "pulse-glow 1.5s infinite" : "none"
          }} />
          {wsStatus === "connected" ? "LIVE ●" : wsStatus === "connecting" ? "CONNECTING…" : "LIVE FEED"}
        </button>

        {fileName && (
          <span style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: hasData ? theme.colors.success : theme.colors.danger }}>
            ▸ {fileName} {hasData && rawData.length > 0 ? `· ${rawData.length.toLocaleString()} rows` : ""}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {rawData.length > 0 && (
          <>
            <input
              placeholder="Search alerts..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{
                background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                borderRadius: 6, color: theme.colors.textPrimary,
                fontFamily: theme.fonts.body, fontSize: 13,
                padding: "8px 12px", outline: "none", width: 180
              }}
            />

            <div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>SEVERITY</div>
              <select
                value={filterSev}
                onChange={e => { setFilterSev(e.target.value); setPage(1); }}
                style={{
                  background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                  borderRadius: 6, color: theme.colors.textPrimary,
                  fontFamily: theme.fonts.body, fontSize: 13, padding: "8px 12px", outline: "none"
                }}
              >
                <option value="ALL">All Severities</option>
                {["critical", "high", "medium", "low", "info"].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>

            {actionValues.length > 0 && (
              <div>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>ACTION</div>
                <select
                  value={filterAction}
                  onChange={e => { setFilterAction(e.target.value); setPage(1); }}
                  style={{
                    background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: 6, color: theme.colors.textPrimary,
                    fontFamily: theme.fonts.body, fontSize: 13, padding: "8px 12px", outline: "none"
                  }}
                >
                  <option value="ALL">All Actions</option>
                  {actionValues.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}

            <button
              onClick={exportCSV}
              style={{
                padding: "8px 16px",
                background: "rgba(0,212,255,0.1)",
                border: `1px solid rgba(0,212,255,0.3)`,
                borderRadius: 6,
                color: theme.colors.accent,
                fontFamily: theme.fonts.display,
                fontWeight: 700, fontSize: 11, letterSpacing: 1,
                cursor: "pointer"
              }}
            >EXPORT CSV</button>
          </>
        )}
      </Card>

      {loading && (
        <div style={{ textAlign: "center", padding: 40, fontFamily: theme.fonts.mono, color: theme.colors.danger, fontSize: 14 }}>
          ◌ Parsing alert log...
        </div>
      )}

      {!rawData.length && !loading && activeTab !== "live" && (
        <Card style={{ textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>◉</div>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 18, color: theme.colors.textSecondary, letterSpacing: 2 }}>
            LOAD AN IDS / FIREWALL / SIEM ALERT LOG
          </div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 8 }}>
            CSV or Excel · Auto-detects severity, alert name, IP, port, action, protocol &amp; more<br/>
            Compatible with Snort, Suricata, Palo Alto, Fortinet, Cisco, Splunk exports
          </div>
          <div style={{ marginTop: 20 }}>
            <button onClick={() => { setActiveTab("live"); connectWS(); }} style={{
              padding: "10px 24px", background: "#C63434",
              border: "none", borderRadius: 8,
              color: theme.colors.buttonText, fontFamily: theme.fonts.display,
              fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer"
            }}>OR SWITCH TO LIVE FEED →</button>
          </div>
        </Card>
      )}

      {/* ── Tabs — always visible ──────────────────────────────────────────────── */}
      {(rawData.length > 0 || activeTab === "live") && (
        <>
          {/* Severity tiles */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            {(activeTab === "live" ? [
                { label: "TOTAL RECEIVED", value: liveCount,               color: EXECUTIVE_SUMMARY_COLORS.total },
                { label: "CRITICAL",       value: liveSevCounts.critical,  color: EXECUTIVE_SUMMARY_COLORS.critical },
                { label: "HIGH",           value: liveSevCounts.high,      color: EXECUTIVE_SUMMARY_COLORS.high },
                { label: "MEDIUM",         value: liveSevCounts.medium,    color: EXECUTIVE_SUMMARY_COLORS.medium },
                { label: "LOW",            value: liveSevCounts.low,       color: EXECUTIVE_SUMMARY_COLORS.low },
                { label: "INFO",           value: liveSevCounts.info,      color: EXECUTIVE_SUMMARY_COLORS.info },
              ] : [
                { label: "TOTAL ALERTS", value: filteredData.length.toLocaleString(), color: EXECUTIVE_SUMMARY_COLORS.total },
                { label: "CRITICAL",     value: severityCounts.critical, color: EXECUTIVE_SUMMARY_COLORS.critical },
                { label: "HIGH",         value: severityCounts.high,     color: EXECUTIVE_SUMMARY_COLORS.high },
                { label: "MEDIUM",       value: severityCounts.medium,   color: EXECUTIVE_SUMMARY_COLORS.medium },
                { label: "LOW",          value: severityCounts.low,      color: EXECUTIVE_SUMMARY_COLORS.low },
                { label: "INFO",         value: severityCounts.info,     color: EXECUTIVE_SUMMARY_COLORS.info },
              ]
            ).map(({ label, value, color }) => (
              <div key={label} style={{ background: `${color}11`, border: `1px solid ${color}33`, borderRadius: 8, padding: "12px 20px", textAlign: "center", minWidth: 110 }}>
                <div style={{ fontFamily: theme.fonts.display, fontSize: 24, fontWeight: 700, color }}>
                  {typeof value === "number" ? value.toLocaleString() : value}
                </div>
                <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1, marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* ── Tabs ──────────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "10px 20px", background: "transparent", border: "none",
                  borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.danger}` : "2px solid transparent",
                  color: activeTab === tab.id
                    ? tab.id === "live" ? SEVERITY_COLORS.critical : theme.colors.danger
                    : theme.colors.textMuted,
                  fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  cursor: "pointer", transition: "all 0.2s"
                }}
              >{tab.label.toUpperCase()}</button>
            ))}
          </div>

          {/* ═══════════ TAB: ALERT FEED ═══════════ */}
          {activeTab === "alerts" && (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto", maxHeight: 700, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg, position: "sticky", top: 0, zIndex: 2 }}>
                      {["#", "Severity", "Alert Name", "Src IP", "Dst IP", "Dst Port", "Protocol", "Action", "Timestamp", ""].map(h => (
                        <th key={h} style={{
                          padding: "10px 12px", textAlign: "left",
                          fontFamily: theme.fonts.mono, fontSize: 10, letterSpacing: 1,
                          color: theme.colors.textMuted,
                          borderBottom: `1px solid ${theme.colors.cardBorder}`,
                          whiteSpace: "nowrap"
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, i) => (
                      <AlertRow
                        key={(page - 1) * rowsPerPage + i}
                        row={row}
                        detected={detected}
                        index={(page - 1) * rowsPerPage + i}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", borderTop: `1px solid ${theme.colors.cardBorder}`
              }}>
                <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
                  {filteredData.length.toLocaleString()} alerts · Page {page} of {totalPages || 1}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { label: "◀◀", action: () => setPage(1) },
                    { label: "◀",  action: () => setPage(p => Math.max(1, p - 1)) },
                    { label: "▶",  action: () => setPage(p => Math.min(totalPages, p + 1)) },
                    { label: "▶▶", action: () => setPage(totalPages) }
                  ].map(({ label, action }) => (
                    <button key={label} onClick={action} style={{
                      padding: "6px 12px", background: "rgba(0,212,255,0.08)",
                      border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 4,
                      color: theme.colors.accent, fontFamily: theme.fonts.mono, fontSize: 12,
                      cursor: "pointer"
                    }}>{label}</button>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* ═══════════ TAB: SUMMARY ═══════════ */}
          {activeTab === "summary" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Severity bar */}
              {charts.severityBar?.length > 0 && (
                <Card>
                  <ChartTitle>Severity Breakdown</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.severityBar}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 12 }} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.severityBar.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Alert name distribution */}
              {charts.alertNameDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Alert Rules / Signatures</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.alertNameDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fill: theme.colors.textSecondary, fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {charts.alertNameDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Category */}
              {charts.categoryDist?.length > 0 && (
                <Card>
                  <ChartTitle>Alert Categories</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={charts.categoryDist} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={100}
                        label={({ name, percent }) => `${name.slice(0, 12)} ${(percent * 100).toFixed(0)}%`}>
                        {charts.categoryDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Action distribution */}
              {charts.actionDist?.length > 0 && (
                <Card>
                  <ChartTitle>Firewall Actions</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.actionDist}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 12 }} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.actionDist.map((d, i) => (
                          <Cell key={i} fill={
                            d.name.toLowerCase().includes("block") || d.name.toLowerCase().includes("deny") || d.name.toLowerCase().includes("drop")
                              ? theme.colors.danger
                              : d.name.toLowerCase().includes("allow") || d.name.toLowerCase().includes("pass")
                              ? theme.colors.success
                              : C[i % C.length]
                          } />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Device / sensor */}
              {charts.deviceDist?.length > 0 && (
                <Card>
                  <ChartTitle>Alert Source Device / Sensor</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.deviceDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={C[4]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Application */}
              {charts.appDist?.length > 0 && (
                <Card>
                  <ChartTitle>Application Layer Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.appDist}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-20} textAnchor="end" />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.appDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: NETWORK ═══════════ */}
          {activeTab === "network" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Top Attacker IPs */}
              {charts.srcIPDist?.length > 0 && (
                <Card>
                  <ChartTitle>🔴 Top Attacker IPs (Source)</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.srcIPDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={theme.colors.danger} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Top Victim IPs */}
              {charts.dstIPDist?.length > 0 && (
                <Card>
                  <ChartTitle>🟡 Top Victim IPs (Destination)</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.dstIPDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={theme.colors.warning} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* IP x Severity stacked */}
              {charts.ipSeverity?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Top Attacker IPs by Severity Level</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.ipSeverity}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 10 }} angle={-20} textAnchor="end" />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }} />
                      <Bar dataKey="critical" stackId="a" fill={SEVERITY_COLORS.critical} />
                      <Bar dataKey="high"     stackId="a" fill={SEVERITY_COLORS.high} />
                      <Bar dataKey="medium"   stackId="a" fill={SEVERITY_COLORS.medium} />
                      <Bar dataKey="low"      stackId="a" fill={SEVERITY_COLORS.low} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Dest ports */}
              {charts.dstPortDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Targeted Ports</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.dstPortDist} margin={{ bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.dstPortDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Protocol */}
              {charts.protocolDist?.length > 0 && (
                <Card>
                  <ChartTitle>Protocol Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={charts.protocolDist} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {charts.protocolDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Country */}
              {charts.countryDist?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Geographic Source of Attacks</ChartTitle>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={charts.countryDist} margin={{ bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-25} textAnchor="end" interval={0} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={C[3]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: TIMELINE ═══════════ */}
          {activeTab === "timeline" && (
            <Card>
              <ChartTitle>Alert Volume Over Time (Critical / High / Medium)</ChartTitle>
              <ResponsiveContainer width="100%" height={360}>
                <AreaChart data={charts.timeline}>
                  <defs>
                    {["Critical", "High", "Medium"].map((k, i) => (
                      <linearGradient key={k} id={`tgrad${k}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={[SEVERITY_COLORS.critical, SEVERITY_COLORS.high, SEVERITY_COLORS.medium][i]} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={[SEVERITY_COLORS.critical, SEVERITY_COLORS.high, SEVERITY_COLORS.medium][i]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <XAxis dataKey="t" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                  <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }} />
                  <Area dataKey="Critical" stroke={SEVERITY_COLORS.critical} fill="url(#tgradCritical)" strokeWidth={2} dot={false} />
                  <Area dataKey="High"     stroke={SEVERITY_COLORS.high}     fill="url(#tgradHigh)"     strokeWidth={2} dot={false} />
                  <Area dataKey="Medium"   stroke={SEVERITY_COLORS.medium}   fill="url(#tgradMedium)"   strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* ═══════════ TAB: LIVE FEED ═══════════ */}
          {activeTab === "live" && (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              {/* ── Live Feed Control Bar ── */}
              <div style={{ borderBottom: `1px solid ${theme.colors.cardBorder}` }}>

                {/* Mode toggle row */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderBottom: `1px solid ${theme.colors.cardBorder}33` }}>
                  <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1 }}>FEED MODE:</span>

                  {/* Random Sim button */}
                  <button onClick={() => { stopDemo(); setDemoMode(false); }}
                    style={{ padding: "5px 14px", borderRadius: 5, cursor: "pointer", fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                      background: !demoMode ? "rgba(0,212,255,0.15)" : "transparent",
                      border: `1px solid ${!demoMode ? theme.colors.accent : theme.colors.cardBorder}`,
                      color: !demoMode ? theme.colors.accent : theme.colors.textMuted }}>
                    ⚡ RANDOM SIM
                  </button>

                  {/* Demo Mode button */}
                  <button onClick={() => { disconnectWS(); setDemoMode(true); }}
                    style={{ padding: "5px 14px", borderRadius: 5, cursor: "pointer", fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                      background: demoMode ? "rgba(156,39,176,0.2)" : "transparent",
                      border: `1px solid ${demoMode ? "#9C27B0" : theme.colors.cardBorder}`,
                      color: demoMode ? "#CE93D8" : theme.colors.textMuted }}>
                    🎬 DEMO MODE
                  </button>

                  {demoMode && (
                    <span style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: "#CE93D8",
                      padding: "3px 8px", background: "rgba(156,39,176,0.15)",
                      border: "1px solid rgba(156,39,176,0.4)", borderRadius: 4, letterSpacing: 1 }}>
                      ● SIMULATED FEED — FOR DEMONSTRATION PURPOSES
                    </span>
                  )}
                </div>

                {/* Demo Mode: file loader + speed + progress */}
                {demoMode && (
                  <div style={{ padding: "12px 18px", background: "rgba(156,39,176,0.05)", borderBottom: `1px solid ${theme.colors.cardBorder}33` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>

                      {/* File loader */}
                      <label style={{ padding: "7px 16px", background: "rgba(156,39,176,0.15)",
                        border: "1px solid rgba(156,39,176,0.5)", borderRadius: 6, cursor: "pointer",
                        fontFamily: theme.fonts.mono, fontSize: 11, fontWeight: 700, color: "#CE93D8", letterSpacing: 1,
                        display: "flex", alignItems: "center", gap: 6 }}>
                        {demoLoading ? "◌ LOADING..." : "📂 LOAD DATASET (CSV/XLSX)"}
                        <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={loadDemoFile} />
                      </label>

                      {demoFileName && (
                        <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: "#CE93D8" }}>
                          ▸ {demoFileName}
                          <span style={{ color: theme.colors.textMuted, marginLeft: 8 }}>
                            {demoData.length.toLocaleString()} rows
                          </span>
                        </div>
                      )}

                      {/* Speed selector */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                        <span style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>SPEED:</span>
                        {[1, 5, 10, 25, 50, 100].map(s => (
                          <button key={s} onClick={() => {
                            setDemoSpeed(s);
                            if (demoRunning) { stopDemo(); setTimeout(() => startDemo(), 100); }
                          }} style={{ padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                            fontFamily: theme.fonts.mono, fontSize: 10, fontWeight: 700,
                            background: demoSpeed === s ? "rgba(156,39,176,0.3)" : "transparent",
                            border: `1px solid ${demoSpeed === s ? "#9C27B0" : theme.colors.cardBorder}`,
                            color: demoSpeed === s ? "#CE93D8" : theme.colors.textMuted }}>
                            {s}x
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Progress bar */}
                    {demoData.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, marginBottom: 4 }}>
                          <span>Dataset Progress</span>
                          <span>{demoPos.toLocaleString()} / {demoData.length.toLocaleString()} rows ({((demoPos/demoData.length)*100).toFixed(1)}%) — loops continuously</span>
                        </div>
                        <div style={{ height: 4, background: theme.colors.cardBorder, borderRadius: 2 }}>
                          <div style={{ height: 4, borderRadius: 2, background: "linear-gradient(90deg, #9C27B0, #CE93D8)",
                            width: `${Math.max(1,(demoPos/Math.max(1,demoData.length))*100)}%`,
                            transition: "width 0.3s ease" }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Main control row */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", flexWrap: "wrap" }}>

                  {/* Status dot */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: theme.fonts.mono, fontSize: 12 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", display: "inline-block",
                      background: wsStatus === "connected"
                        ? (demoMode ? "#9C27B0" : SEVERITY_COLORS.critical)
                        : wsStatus === "connecting" ? theme.colors.warning
                        : wsStatus === "error" ? theme.colors.danger : "#3D5A80",
                      boxShadow: wsStatus === "connected"
                        ? `0 0 8px ${demoMode ? "#9C27B0" : SEVERITY_COLORS.critical}` : "none",
                      animation: wsStatus === "connected" ? "pulse-glow 1.5s infinite" : "none"
                    }} />
                    <span style={{ color: wsStatus === "connected"
                        ? (demoMode ? "#CE93D8" : SEVERITY_COLORS.critical)
                        : wsStatus === "connecting" ? theme.colors.warning
                        : wsStatus === "error" ? theme.colors.danger : theme.colors.textMuted,
                      fontWeight: 700, letterSpacing: 1 }}>
                      {wsStatus === "connected"
                        ? (demoMode ? "● DEMO RUNNING" : "● LIVE")
                        : wsStatus === "connecting" ? "◌ CONNECTING…"
                        : wsStatus === "error" ? "✕ ERROR" : "○ DISCONNECTED"}
                    </span>
                    {wsStatus === "connected" && (
                      <span style={{ color: theme.colors.textMuted, fontSize: 11 }}>
                        {liveAlerts.length.toLocaleString()} buffered · {liveCount.toLocaleString()} total
                      </span>
                    )}
                  </div>

                  <div style={{ flex: 1 }} />

                  {/* Control buttons */}
                  {demoMode ? (
                    // Demo mode controls
                    demoRunning ? (
                      <>
                        <button onClick={togglePause} style={{ padding: "8px 18px",
                          background: isPaused ? "rgba(255,184,0,0.15)" : "rgba(156,39,176,0.15)",
                          border: `1px solid ${isPaused ? theme.colors.warning : "#9C27B0"}`,
                          borderRadius: 6, color: isPaused ? theme.colors.warning : "#CE93D8",
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                          {isPaused ? "▶ RESUME" : "⏸ PAUSE"}
                        </button>
                        <button onClick={stopDemo} style={{ padding: "8px 18px", background: "rgba(255,61,87,0.1)",
                          border: "1px solid rgba(255,61,87,0.4)", borderRadius: 6, color: theme.colors.danger,
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                          ⏹ STOP
                        </button>
                        <button onClick={resetDemo} style={{ padding: "8px 18px", background: "rgba(107,141,176,0.1)",
                          border: "1px solid rgba(107,141,176,0.3)", borderRadius: 6, color: theme.colors.textMuted,
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                          ↺ RESET
                        </button>
                      </>
                    ) : (
                      <button onClick={startDemo} disabled={!demoData.length || demoLoading}
                        style={{ padding: "8px 24px",
                          background: demoData.length ? "linear-gradient(135deg, #7B1FA2, #9C27B0)" : "rgba(107,141,176,0.1)",
                          border: "none", borderRadius: 6, color: demoData.length ? "#fff" : theme.colors.textMuted,
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1,
                          cursor: demoData.length ? "pointer" : "not-allowed" }}>
                        {demoData.length ? "▶ START DEMO" : "LOAD DATASET FIRST"}
                      </button>
                    )
                  ) : (
                    // Random sim controls
                    wsStatus === "disconnected" || wsStatus === "error" ? (
                      <button onClick={connectWS} style={{ padding: "10px 20px",
                        background: "#C63434",
                        border: "none", borderRadius: 8, color: theme.colors.buttonText,
                        fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                        CONNECT
                      </button>
                    ) : (
                      <>
                        <button onClick={togglePause} style={{ padding: "8px 18px",
                          background: isPaused ? "rgba(255,184,0,0.15)" : "rgba(0,212,255,0.1)",
                          border: `1px solid ${isPaused ? theme.colors.warning : theme.colors.accent}`,
                          borderRadius: 6, color: isPaused ? theme.colors.warning : theme.colors.accent,
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                          {isPaused ? "▶ RESUME" : "⏸ PAUSE"}
                        </button>
                        <button onClick={disconnectWS} style={{ padding: "8px 18px", background: "rgba(255,61,87,0.1)",
                          border: "1px solid rgba(255,61,87,0.4)", borderRadius: 6, color: theme.colors.danger,
                          fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                          DISCONNECT
                        </button>
                      </>
                    )
                  )}

                  <button onClick={clearLive} style={{ padding: "8px 14px", background: "rgba(107,141,176,0.1)",
                    border: "1px solid rgba(107,141,176,0.3)", borderRadius: 6, color: theme.colors.textMuted,
                    fontFamily: theme.fonts.display, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                    CLEAR
                  </button>
                </div>
              </div>

              {/* Disconnected placeholder */}
              {(wsStatus === "disconnected" || wsStatus === "error") && liveAlerts.length === 0 && (
                <div style={{ textAlign: "center", padding: "60px 40px" }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>◉</div>
                  <div style={{ fontFamily: theme.fonts.display, fontSize: 16, color: theme.colors.textSecondary, letterSpacing: 2, marginBottom: 8 }}>
                    LIVE FEED — STANDBY
                  </div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginBottom: 6 }}>
                    Connects to <span style={{ color: theme.colors.accent }}>wss://nexgen-backend/ws/stream</span>
                  </div>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
                    Receives real-time alerts from IDS agents, Zeek, Suricata, Cisco, Fortinet, and custom sources
                  </div>
                  {wsStatus === "error" && (
                    <div style={{ marginTop: 14, fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.danger }}>
                      ✕ Connection failed — check backend is running and WebSocket endpoint is active
                    </div>
                  )}
                </div>
              )}

              {/* Connecting spinner */}
              {wsStatus === "connecting" && (
                <div style={{ textAlign: "center", padding: "40px", fontFamily: theme.fonts.mono, fontSize: 13, color: theme.colors.warning }}>
                  ◌ Establishing WebSocket connection…
                </div>
              )}

              {/* Alert scroll feed */}
              {liveAlerts.length > 0 && (
                <div style={{ maxHeight: 620, overflowY: "auto", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                  {isPaused && (
                    <div style={{ padding: "8px 18px", background: "rgba(255,184,0,0.1)", borderBottom: `1px solid rgba(255,184,0,0.3)`, color: theme.colors.warning, fontSize: 11, letterSpacing: 1 }}>
                      ⏸ PAUSED — incoming alerts are being dropped
                    </div>
                  )}
                  {liveAlerts.map((alert, i) => {
                    const color = SEVERITY_COLORS[alert._sev] || SEVERITY_COLORS.unknown;
                    return (
                      <div key={i} style={{
                        display: "grid",
                        gridTemplateColumns: "60px 80px 160px 130px 130px 90px 1fr",
                        gap: 0,
                        padding: "7px 18px",
                        borderBottom: `1px solid ${theme.colors.cardBorder}22`,
                        background: i === 0 ? `${color}14` : i % 2 === 0 ? theme.colors.rowAlt : "transparent",
                        alignItems: "center",
                        animation: i === 0 ? "fadeInUp 0.2s ease" : "none"
                      }}>
                        <span style={{ color: theme.colors.textMuted, fontSize: 10 }}>#{liveCount - i}</span>
                        <SeverityBadge level={alert._sev} />
                        <span style={{ color, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {alert.attack_type || alert.signature || alert.event_type || "Unknown"}
                        </span>
                        <span style={{ color: theme.colors.danger }}>
                          {alert.src_ip || alert.source_ip || "—"}
                        </span>
                        <span style={{ color: theme.colors.warning }}>
                          {alert.dst_ip || alert.dest_ip || "—"}
                        </span>
                        <span style={{ color: theme.colors.textSecondary }}>
                          {alert.dst_port || alert.dest_port || "—"}
                        </span>
                        <span style={{ color: theme.colors.textMuted, fontSize: 10 }}>{alert._ts}</span>
                      </div>
                    );
                  })}
                  <div ref={feedEndRef} />
                </div>
              )}

              {/* Column headers */}
              {liveAlerts.length > 0 && (
                <div style={{
                  display: "grid", gridTemplateColumns: "60px 80px 160px 130px 130px 90px 1fr",
                  gap: 0, padding: "8px 18px", background: theme.colors.inputBg,
                  borderTop: `1px solid ${theme.colors.cardBorder}`,
                  fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted,
                  letterSpacing: 1, position: "sticky", bottom: 0
                }}>
                  {["#", "SEVERITY", "ATTACK TYPE", "SRC IP", "DST IP", "PORT", "TIME"].map(h => (
                    <span key={h}>{h}</span>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Footer */}
          <div style={{
            textAlign: "center", marginTop: 32,
            fontFamily: theme.fonts.mono, fontSize: 10,
            color: theme.colors.textMuted, paddingBottom: 20
          }}>
            NexGen by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com
          </div>
        </>
      )}
    </PageLayout>
  );
}
